'use client'

import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'
import { type ReactNode, Suspense, useState } from 'react'

import AContentCardAddAdditional from '@/_components/custom/cards/content-card/components/a-content-card-add-additional'
import { AContentCardOrderButton } from '@/_components/custom/cards/content-card/components/a-content-card-content-with-additional'
import ZSuspenseLoading from '@/_components/universal/z-suspense-loading'
import { IAnnualProgram, PROGRAM_CARD_TYPE_ENUM } from '@/_types'

import ZAPDetailButton from './z-ap-detail-button'

/**
 * Props for the ZAPDetailChildProgram component.
 */
interface IZAPDetailChildProgramProps {
	/** Current selected program (contains _childProgram if modules available) */
	annualProgram: IAnnualProgram
	/** Primary category/gender for styling ('male' or 'female') */
	primaryCategory: 'male' | 'female' | 'child'
	title: string
	text?: string
	buttons?: {
		text: string
		onClick: () => void
		scrollToId: string
		isPrimary: boolean
		color: 'child' | 'blue' | 'cyclamen'
	}[]
	image: {
		src: string
		alt: string
	}
	footerText?: string
}

/**
 * Props for the ProgramCard component.
 */
interface IProgramCardProps {
	/** Annual program with child program/modules */
	annualProgram: IAnnualProgram
}

/**
 * Program card component that displays module selection and order button.
 * Handles module selection state and allows users to add optional modules.
 *
 * @param annualProgram - Program data with child program
 */
/** Parses HTML string and renders <a> as Next.js <Link>, rest as HTML. */
function footerWithLinks(html: string): ReactNode[] {
	const parts: ReactNode[] = []
	const re = /<a\s+href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/gi
	let lastIndex = 0
	let match
	let key = 0
	while ((match = re.exec(html)) !== null) {
		const before = html.slice(lastIndex, match.index)
		if (before) parts.push(<span key={key++} dangerouslySetInnerHTML={{ __html: before }} />)
		parts.push(
			<Link key={key++} href={match[1]}>
				<span dangerouslySetInnerHTML={{ __html: match[2] }} />
			</Link>
		)
		lastIndex = re.lastIndex
	}
	const after = html.slice(lastIndex)
	if (after) parts.push(<span key={key++} dangerouslySetInnerHTML={{ __html: after }} />)
	return parts
}

const ProgramCard = ({ annualProgram }: IProgramCardProps) => {
	const [selectedModules, setSelectedModules] = useState<any[]>([])

	const handleModulesChange = (mod: any, isChecked: boolean) => {
		setSelectedModules(prev =>
			isChecked
				? [...prev.filter(m => m.id !== mod.id), mod]
				: prev.filter(m => m.id !== mod.id)
		)
	}

	return (
		<div className='medante-vital__program__card'>
			<div className='medante-vital__program__card__modules'>
				<AContentCardAddAdditional
					data={annualProgram}
					initialExpanded={true}
					onModulesChange={handleModulesChange}
					selectedModuleIds={selectedModules.map(m => m.id)}
				/>
			</div>

			<AContentCardOrderButton
				program={annualProgram}
				selectedModules={selectedModules}
				type={PROGRAM_CARD_TYPE_ENUM.ANNUAL_PROGRAM}
			/>
		</div>
	)
}

/**
 * Universal child program section component for annual program detail pages.
 * Displays optional modules section with preventive examination information.
 *
 * Features:
 * - Gender toggle buttons
 * - Module selection interface
 * - Order button for child program
 * - Preventive examination description
 * - Responsive image display
 *
 * Only renders if the program has a _childProgram property.
 * Shared across Medante Balance, Mini, and Vital programs.
 *
 * @param annualProgram - Program data (must have _childProgram to render)
 * @param primaryCategory - Gender category for styling ('male' = blue, 'female' = cyclamen)
 *
 * @example
 * ```typescript
 * <ZAPDetailChildProgram
 *   annualProgram={currentProgram}
 *   primaryCategory='female'
 * />
 * ```
 */
export default function ZAPDetailChildProgram({
	annualProgram,
	primaryCategory,
	title,
	text,
	image,
	buttons = [],
	footerText
}: IZAPDetailChildProgramProps) {
	return (
		<section
			className={clsx(
				'medante-vital__program mb-[50px]',
				`medante-vital__program--${primaryCategory}`
			)}
			id='kupit-program'
		>
			<div className='wrapper2 pdt-60lg pdt-90 pdb-80 pdb-40lg'>
				<div className='medante-vital__program__header'>
					<h2 dangerouslySetInnerHTML={{ __html: title }} />

					{text && (
						<p suppressHydrationWarning dangerouslySetInnerHTML={{ __html: text }} />
					)}

					{buttons.length > 0 && (
						<div className='medante-vital__program__header__buttons'>
							{buttons.map(button => (
								<ZAPDetailButton
									key={button.text}
									text={button.text}
									onClick={button.onClick}
									scrollToId={button.scrollToId}
									isPrimary={button.isPrimary}
									color={button.color}
								/>
							))}
						</div>
					)}
				</div>

				<div className='medante-vital__program__content'>
					<div className='medante-vital__program__content__left'>
						<Suspense fallback={<ZSuspenseLoading />}>
							<ProgramCard annualProgram={annualProgram} />
						</Suspense>
					</div>

					<div className='medante-vital__program__content__right'>
						<div className='medante-vital__program__content__image'>
							<Image
								unoptimized={true}
								src={image.src}
								alt={image.alt}
								width={727}
								height={483}
								quality={100}
							/>
						</div>
					</div>
				</div>

				{footerText && (
					<div className='medante-vital__program__footer'>
						<p>{footerWithLinks(footerText)}</p>
					</div>
				)}
			</div>
		</section>
	)
}
